import React, {
  useEffect, useRef, useState, useMemo,
} from 'react';
import classNames from 'classnames';
import type { ISubLink } from 'components/common/StickyTabsNav/types';
import type { IWithChildren } from 'types';
import { useScrollMonitor } from 'util/useScrollMonitor';
import { TabLink } from 'components/blocks/TabNav/Link';

import commonStyles from 'components/common/StyleModules/TabMenu.module.scss';
import styles from './styles.module.scss';

interface IProps<Key extends string> {
  linkItemsRight: Array<ISubLink<Key>> | null,
  linkItemsLeft: Array<ISubLink<Key>>,
  link: Key,
}

export const TabsNavigationRedesign = <Key extends string>({
  link,
  linkItemsLeft,
  linkItemsRight,
  children,
}: IProps<Key> & IWithChildren,
) => {
  const linksRef = useRef<HTMLDivElement>(null);
  const divRef = useRef<HTMLDivElement>(null);
  const stickyRef = useRef<HTMLDivElement>(null);

  const [activeLinksRef, setActiveLinksRef] = useState<HTMLAnchorElement | null>(null);
  const [isStuck, setIsStuck] = useState<boolean>();
  const links = useMemo<Array<ISubLink<Key>>>(
    () => [...linkItemsLeft, ...(linkItemsRight || [])],
    [linkItemsLeft, linkItemsRight],
  );

  useScrollMonitor(divRef, setIsStuck, isStuck, 46);

  useEffect(() => {
    if (linksRef.current && activeLinksRef) {
      const container = linksRef.current;
      container.scrollTo({ left: activeLinksRef.getBoundingClientRect().left });
    }
  }, [activeLinksRef]);

  useEffect(() => {
    const scrollMonitor = () => {
      if (linksRef.current && stickyRef.current) {
        stickyRef.current.style.height = `${linksRef.current.offsetHeight}px`;
      }
    };
    window.addEventListener('resize', scrollMonitor);

    scrollMonitor();
    return () => {
      window.removeEventListener('resize', scrollMonitor);
    };
  }, [isStuck]);

  return (
    <>
      <div className={classNames(commonStyles.related, commonStyles.showLG)}>
        {children}
      </div>
      <nav
        className={classNames(styles.wrapper, isStuck ? styles.fixed : '')}
        ref={divRef}
      >
        <div
          className={styles.sticky}
          ref={stickyRef}
        />
        <div className={classNames(styles.nav, styles.lg)}>
          <div
            className={classNames('container', styles.linksContainer, styles.lg)}
            ref={linksRef}
          >
            <div className={styles.leftPart}>
              {linkItemsLeft.map((tI) => (
                <TabLink
                  onActive={setActiveLinksRef}
                  href={tI.href}
                  current={tI.uuid === link}
                  external={!!tI.isExternal}
                  key={tI.uuid}
                  isRedesigned
                >
                  {tI.title}
                </TabLink>
              ))}
            </div>
            {linkItemsRight && (
              <>
                <span className={classNames(styles.divider, styles.lg)}>|</span>
                <div className={styles.rightPart}>
                  {linkItemsRight.map((tI) => (
                    <TabLink
                      onActive={setActiveLinksRef}
                      href={tI.href}
                      current={tI.uuid === link}
                      external={!!tI.isExternal}
                      forceReload={!!tI.forceReload}
                      key={tI.uuid}
                      isRedesigned
                    >
                      {tI.title}
                    </TabLink>
                  ))}
                </div>
              </>
            )}
          </div>
          <div
            className={classNames(styles.linksContainer, styles.md)}
            ref={linksRef}
          >
            {links.map((tI, index) => (
              <React.Fragment key={tI.uuid}>
                <TabLink
                  onActive={setActiveLinksRef}
                  href={tI.href}
                  current={tI.uuid === link}
                  external={!!tI.isExternal}
                  isRedesigned
                  forceReload={!!tI.forceReload}
                >
                  {tI.title}
                </TabLink>
                {index !== links.length - 1 && (
                  <span className={classNames(styles.divider, styles.md)}>/</span>
                )}
              </React.Fragment>
            ))}
          </div>
        </div>
      </nav>
      <div className={classNames(commonStyles.related, commonStyles.hideLG)}>
        {children}
      </div>
    </>
  );
};
